import { isOpsSignalEnabled } from '@/lib/opsSignalEnabled';

type OpsSignalKind =
  | 'basket_api_error'
  | 'checkout_api_error'
  | 'gw_downstream_error'
  | 'client_error';

const lastSentAtByKey = new Map<string, number>();
const THROTTLE_MS = 3000;

function truncate(s: string | undefined, max: number): string {
  if (!s) return '';
  return s.length <= max ? s : `${s.slice(0, max)}…`;
}

/** Map GW path → ops-signal kind (DB/cache kullanmayan in-memory aggregator). */
export function classifyApiPathKind(pathOrUrl: string): OpsSignalKind | null {
  const p = pathOrUrl.split('?')[0] || '';
  if (p.includes('/basket/')) return 'basket_api_error';
  if (
    p.includes('/order/start') ||
    p.includes('/order/complete') ||
    p.includes('/order/masterpass') ||
    p.includes('/callback/')
  ) {
    return 'checkout_api_error';
  }
  return null;
}

function shouldSend(key: string): boolean {
  const now = Date.now();
  const prev = lastSentAtByKey.get(key) || 0;
  if (now - prev < THROTTLE_MS) return false;
  lastSentAtByKey.set(key, now);
  return true;
}

async function postToOpsSignalDirect(payload: Record<string, unknown>): Promise<void> {
  const base = (process.env.OPS_SIGNAL_URL || '').replace(/\/+$/, '');
  if (!base) return;

  const url = base.endsWith('/v1/events') ? base : `${base}/v1/events`;
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const key = process.env.OPS_SIGNAL_INGEST_KEY;
  if (key) headers['X-Ops-Signal-Key'] = key;

  const ctrl = new AbortController();
  const tid = setTimeout(() => ctrl.abort(), 2500);
  try {
    await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
      signal: ctrl.signal,
    });
  } catch {
    /* fire-and-forget */
  } finally {
    clearTimeout(tid);
  }
}

export function reportOpsSignal(opts: {
  kind: OpsSignalKind;
  message: string;
  tags?: Record<string, string | number | boolean>;
}) {
  if (!isOpsSignalEnabled()) return;

  const payload = {
    kind: opts.kind,
    source: 'hzc-web-site',
    message: truncate(opts.message, 1500),
    ts: Date.now(),
    tags: opts.tags,
  };

  const throttleKey = `${opts.kind}:${String(opts.tags?.endpoint || opts.message || '').slice(0, 40)}`;
  if (!shouldSend(throttleKey)) return;

  if (typeof window !== 'undefined') {
    const body = JSON.stringify(payload);
    try {
      if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
        const blob = new Blob([body], { type: 'application/json' });
        if (navigator.sendBeacon('/api/ops-signal', blob)) return;
      }
    } catch {
      /* fall through */
    }
    void fetch('/api/ops-signal', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
      credentials: 'same-origin',
    }).catch(() => {});
    return;
  }

  void postToOpsSignalDirect(payload);
}

export function reportApiFailureFromRequester(opts: {
  endpoint: string;
  method?: string;
  httpStatus?: number;
  statusCode?: number;
  message?: string;
}) {
  const kind = classifyApiPathKind(opts.endpoint);
  if (!kind) return;

  const parts = [
    opts.method || 'GET',
    opts.endpoint,
    opts.httpStatus ? `HTTP ${opts.httpStatus}` : '',
    opts.statusCode != null && opts.statusCode !== 0 ? `meta ${opts.statusCode}` : '',
    opts.message || '',
  ].filter(Boolean);

  reportOpsSignal({
    kind,
    message: parts.join(' '),
    tags: {
      endpoint: opts.endpoint,
      method: opts.method || '',
      httpStatus: opts.httpStatus ?? 0,
      statusCode: opts.statusCode ?? -1,
    },
  });
}
